import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ButtonsSection from "components/ui/buttons/ButtonsSection";
import { VaulDrawerShell } from "components/ui/drawer/VaulDrawerShell";
import { useSubscriptionStatus } from "../../home/hooks/useSubscriptionStatus";
import { useStripeSubscriptionStatus } from "../hooks/useStripeSubscriptionStatus";
import { useSettingsContextData } from "../hooks/useSettingsContextData";
import styles from "./Settings.module.css";
import { isDevEnvironment } from "../../../env/runtimeEnv";
import { availableOptions, accountOptions } from "../config/menuOptions";
import LeaveTeamModal from "./LeaveTeamModal";
import { useTeam } from "../../../contexts/TeamContext";
import { TeamService } from "../../../services/TeamService";
import SettingsOptionsList from "./SettingsOptionsList";
import { Option } from "../types";
export default function Settings() {
  const navigate = useNavigate();

  const { isLoading, userId, email, logout, organizationId } =
    useSettingsContextData();
  const [isLeaveTeamModalOpen, setIsLeaveTeamModalOpen] = useState(false);
  const [isLeavingTeam, setIsLeavingTeam] = useState(false);
  const { isLead, teamId, teamName, setTeamId, organizationName } = useTeam();

  const {
    hasActiveSubscription,
    leadUid,
  } = useSubscriptionStatus({ userId });
  const { hasStripeSubscription: hasUserStripeSubscription } =
    useStripeSubscriptionStatus(userId);
  const { hasStripeSubscription: hasLeadStripeSubscription } =
    useStripeSubscriptionStatus(leadUid);
  const hasStripeSubscription =
    hasUserStripeSubscription || hasLeadStripeSubscription;

  const visibleAvailableOptions = availableOptions
    .filter(
      (option) =>
        !(isDevEnvironment && option.forDevEnv === false) &&
        !(option.forLead === false && isLead === true)
    )
    .map((option) =>
      option.link === "/app/settings/teams"
        ? {
            ...option,
            state: { ...option.state, hasStripeSubscription },
          }
        : option
    );

  const handleLeaveTeam = async () => {
    setIsLeavingTeam(true);
    try {
      await TeamService.removeTeamMemberFromTeam(teamId, userId);
      setIsLeaveTeamModalOpen(false);

      setTeamId(null);
      navigate("/app/join-team", { replace: true });
    } catch (error) {
      console.error("❌ Error leaving team:", error);
      alert("Failed to leave team. Please try again.");
    } finally {
      setIsLeavingTeam(false);
    }
  };

  const handleAccountOptionClick = (option: Option) => {
    switch (option.function) {
      case "setIsLeaveTeamModalOpen":
        setIsLeaveTeamModalOpen(true);
        return;
      default:
        if (option.link) {
          navigate(
            option.link,
            option.state ? { state: option.state } : undefined
          );
        }
    }
  };

  if (isLoading) {
    return (
      <div className={styles.settingsLoading}>
        <p>Loading user data...</p>
      </div>
    );
  }

  return (
    <div className="root">
      <div className={styles["header-container"]}>
        <span className="page-title">Settings</span>
      </div>
      <div>
        {email && (
          <div className={styles.settingsContainer}>
            <div className={styles.settingsEmailRow}>
              <p className={styles.settingsLink}>{email}</p>
            </div>
            {!isDevEnvironment &&
              email &&
              !hasActiveSubscription &&
              !organizationId && (
                <p
                  onClick={() => navigate("/app/upgrade")}
                  className={styles.settingsLink}
                >
                  Subscription
                </p>
              )}
          </div>
        )}
        <SettingsOptionsList
          options={accountOptions}
          handleClick={handleAccountOptionClick}
        />
        <SettingsOptionsList
          options={visibleAvailableOptions}
          handleClick={handleAccountOptionClick}
        />

        <div className="buttons-wrapper">
          <ButtonsSection />
        </div>

        {isLeaveTeamModalOpen && (
          <VaulDrawerShell
            open={isLeaveTeamModalOpen}
            onOpenChange={() => {
              setIsLeaveTeamModalOpen(!isLeaveTeamModalOpen);
            }}
          >
            <LeaveTeamModal
              setIsLeaveTeamModalOpen={setIsLeaveTeamModalOpen}
              handleLeaveTeam={handleLeaveTeam}
              isLeavingTeam={isLeavingTeam}
              embedded={false}
            />
          </VaulDrawerShell>
        )}
      </div>
    </div>
  );
}
